import { AssetType } from '@amzn/fire-tv-campaign-automation-service';

import {
  PreviewConfigBase,
  PreviewControlType,
  PreviewControlKey,
  PreviewControlValue,
  PreviewGenerationStrategy,
  FireTabletsPreviewState,
  TabletSize,
  TabletOrientation,
  TabletDeviceType,
  TabletControlKey,
} from '../types';

import { DEVICE_TYPE_SIZE_MAP } from './constants';
import { TABLET_10INCH_LANDSCAPE_FOS } from './states/10Inch_Landscape_FOS';
import { TABLET_10INCH_PORTRAIT_FOS } from './states/10Inch_Portrait_FOS';
import { TABLET_11INCH_LANDSCAPE_FOS } from './states/11Inch_Landscape_FOS';
import { TABLET_11INCH_LANDSCAPE_KH } from './states/11Inch_Landscape_KH';
import { TABLET_11INCH_PORTRAIT_FOS } from './states/11Inch_Portrait_FOS';
import { TABLET_11INCH_PORTRAIT_KH } from './states/11Inch_Portrait_KH';
import { TABLET_12INCH_LANDSCAPE_KH } from './states/12Inch_Landscape_KH';
import { TABLET_12INCH_PORTRAIT_KH } from './states/12Inch_Portrait_KH';
import { TABLET_7INCH_LANDSCAPE_FOS } from './states/7Inch_Landscape_FOS';
import { TABLET_7INCH_PORTRAIT_FOS } from './states/7Inch_Portrait_FOS';
import { TABLET_8INCH_LANDSCAPE_FOS } from './states/8Inch_Landscape_FOS';
import { TABLET_8INCH_LANDSCAPE_KH } from './states/8Inch_Landscape_KH';
import { TABLET_8INCH_PORTRAIT_FOS } from './states/8Inch_Portrait_FOS';
import { TABLET_8INCH_PORTRAIT_KH } from './states/8Inch_Portrait_KH';

/**
 * FireTablet Feature Rotator Preview Configuration
 *
 * Defines preview behavior for FireTablet Feature Rotator placement
 *
 * Variants: 9 total (7", 8", 10", 11" x Portrait/Landscape x FOS)
 * - Each variant has unique dimensions and shell overlay
 * - Background: Center crop with right focus (shiftFactor: 0.85)
 * - Logo: Fit within bounds with top alignment
 *
 * Behavior:
 * - Strategy: CURRENT_VARIANT - generates preview only for selected variant
 * - Controls: Dropdowns for size/deviceType, button group for orientation
 * - State resolution: Combines size_orientation_deviceType (e.g., "10Inch_Landscape_FOS")
 * - Initial URLs: Empty (generates on first selection)
 *
 * Default: 10Inch_Landscape_FOS
 */
export const FIRE_TABLET_FEATURE_ROTATOR_CONFIG: PreviewConfigBase = {
  defaultState: FireTabletsPreviewState.TABLET_10INCH_LANDSCAPE_FOS,
  controls: [
    {
      type: PreviewControlType.DROPDOWN,
      key: TabletControlKey.DEVICE_TYPE,
      label: 'Device Type',
      options: [
        { label: 'Fire OS', value: TabletDeviceType.FOS },
        { label: 'Kitty Hawk', value: TabletDeviceType.KH },
      ],
      defaultValue: TabletDeviceType.FOS,
    },
    {
      type: PreviewControlType.DROPDOWN,
      key: TabletControlKey.SIZE,
      label: 'Size',
      options: (values) => {
        const deviceType = values[TabletControlKey.DEVICE_TYPE] as TabletDeviceType;
        const allowedSizes =
          DEVICE_TYPE_SIZE_MAP[deviceType] || DEVICE_TYPE_SIZE_MAP[TabletDeviceType.FOS];

        return allowedSizes.map((size) => ({
          label: size.replace('Inch', '"'),
          value: size,
        }));
      },
      defaultValue: TabletSize.TEN_INCH,
    },
    {
      type: PreviewControlType.BUTTON_GROUP,
      key: TabletControlKey.ORIENTATION,
      label: 'Orientation',
      options: [
        { label: 'Portrait', value: TabletOrientation.PORTRAIT },
        { label: 'Landscape', value: TabletOrientation.LANDSCAPE },
      ],
      defaultValue: TabletOrientation.LANDSCAPE,
    },
  ],
  behavior: {
    previewGeneration: PreviewGenerationStrategy.CURRENT_VARIANT,
    stateResolution: (values: Record<PreviewControlKey, PreviewControlValue>) => {
      const size = values[TabletControlKey.SIZE] ?? TabletSize.TEN_INCH;
      const orientation = values[TabletControlKey.ORIENTATION] ?? TabletOrientation.LANDSCAPE;
      const deviceType = values[TabletControlKey.DEVICE_TYPE] ?? TabletDeviceType.FOS;
      return `${size}_${orientation}_${deviceType}` as FireTabletsPreviewState;
    },
  },
  stateConfigs: {
    [FireTabletsPreviewState.TABLET_7INCH_PORTRAIT_FOS]: TABLET_7INCH_PORTRAIT_FOS,
    [FireTabletsPreviewState.TABLET_7INCH_LANDSCAPE_FOS]: TABLET_7INCH_LANDSCAPE_FOS,
    [FireTabletsPreviewState.TABLET_8INCH_PORTRAIT_FOS]: TABLET_8INCH_PORTRAIT_FOS,
    [FireTabletsPreviewState.TABLET_8INCH_LANDSCAPE_FOS]: TABLET_8INCH_LANDSCAPE_FOS,
    [FireTabletsPreviewState.TABLET_10INCH_PORTRAIT_FOS]: TABLET_10INCH_PORTRAIT_FOS,
    [FireTabletsPreviewState.TABLET_10INCH_LANDSCAPE_FOS]: TABLET_10INCH_LANDSCAPE_FOS,
    [FireTabletsPreviewState.TABLET_11INCH_PORTRAIT_FOS]: TABLET_11INCH_PORTRAIT_FOS,
    [FireTabletsPreviewState.TABLET_11INCH_LANDSCAPE_FOS]: TABLET_11INCH_LANDSCAPE_FOS,
    [FireTabletsPreviewState.TABLET_8INCH_PORTRAIT_KH]: TABLET_8INCH_PORTRAIT_KH,
    [FireTabletsPreviewState.TABLET_8INCH_LANDSCAPE_KH]: TABLET_8INCH_LANDSCAPE_KH,
    [FireTabletsPreviewState.TABLET_11INCH_PORTRAIT_KH]: TABLET_11INCH_PORTRAIT_KH,
    [FireTabletsPreviewState.TABLET_11INCH_LANDSCAPE_KH]: TABLET_11INCH_LANDSCAPE_KH,
    [FireTabletsPreviewState.TABLET_12INCH_PORTRAIT_KH]: TABLET_12INCH_PORTRAIT_KH,
    [FireTabletsPreviewState.TABLET_12INCH_LANDSCAPE_KH]: TABLET_12INCH_LANDSCAPE_KH,
  },
  downloads: {
    includeAssets: true,
    includePreviews: true,
    supportedAssets: [AssetType.BACKGROUND_IMAGE, AssetType.LOGO],
  },
};
